import React, { forwardRef } from 'react';
import type { View } from 'react-native';

import { Button } from '@squire/ui';

import { CloseIcon } from '@/components/_icons';

import { styles } from './styles';
import type { CloseButtonProps } from './types';

/**
 * The close affordance. Size, variant, icon and role are fixed here on purpose
 * so that call sites cannot drift: they pass what the button means (its labels)
 * and where it sits (`style`), never how it looks. `inverse` is the one
 * exception, and it only re-picks the color pair so the same button stays
 * legible on an inverted surface — see `types.ts`.
 */
export const CloseButton = forwardRef<View, CloseButtonProps>(
  (
    {
      accessibilityLabel,
      accessibilityHint,
      onPress,
      inverse = false,
      iconColor,
      disabled,
      style,
      testID,
      ...props
    },
    ref,
  ) => (
    <Button
      {...props}
      ref={ref}
      size="small"
      variant="secondary"
      onPress={onPress}
      disabled={disabled}
      style={[inverse && styles.inverseButton, style]}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      testID={testID}
    >
      <CloseIcon
        color={iconColor ?? (inverse ? 'iconPrimaryInverse' : 'iconSecondary')}
      />
    </Button>
  ),
);

CloseButton.displayName = 'CloseButton';
